import Alpine from 'alpinejs';

// Grid animation controller
let gridAnimationFrame = null;
let gridStepTimeout = null;
let fadeInterval = null;

// Logging helper
function l(m) {
  if(window.location.hostname.split('.').at(-1) == 'test') {
    console.log(m);
  }
}

// sessionStorage key — intro plays once per browser session
const INTRO_STORAGE_KEY = 'introPlayed';

export function shouldPlayIntro() {
  try {
    return !sessionStorage.getItem(INTRO_STORAGE_KEY);
  } catch (e) {
    return true;
  }
}

function markIntroPlayed() {
  try {
    sessionStorage.setItem(INTRO_STORAGE_KEY, '1');
  } catch (e) {
    // ignore
  }
}

export function homeIntro() {
  return {
    // State
    introCentered: true,
    contentVisible: false,
    navVisible: false,
    gridOpacity: 1,
    gridPointerEvents: 'auto',
    animationRunning: false,

    // Grid animation state
    targetWidth: window.innerWidth,
    targetHeight: window.innerHeight,
    currentWidth: window.innerWidth,
    currentHeight: window.innerHeight,
    currentStep: 0,

    // Grid scale sequence
    sequence: [
      { x: 2.15, y: 1.45 },
      { x: 5, y: 2.6 },
      { x: 1.67, y: 4.35 },
      { x: 5, y: 1.2 },
      { x: 1, y: 1 },
      { x: 14, y: 10.5 }
    ],

    // Initialize
    init() {
      if (shouldPlayIntro()) {
        l('🎬 Intro animation starting');
        this.startIntro();
      } else {
        l('⏭️ Intro skipped (played recently)');
        this.skipIntro();
      }
    },

    // Skip intro — go straight to completed state
    skipIntro() {
      this.cleanupAnimations();
      this.introCentered = false;
      this.navVisible = true;
      this.contentVisible = true;
      this.gridOpacity = 0;
      this.gridPointerEvents = 'none';
    },

    // Start the full intro animation sequence
    startIntro() {
      this.resetState();
      this.animationRunning = true;

      const grid = this.$refs.grid;
      if (!grid) {
        l('❌ Grid not found');
        return;
      }

      l('✅ Grid found, starting animation');

      // Start grid animation
      this.animateGrid(grid);
      this.nextStep();

      // Fade the header logo in at its centered intro position.
      setTimeout(() => {
        l('📝 Show header logo (centered)');
        this.navVisible = true;
      }, 80);

      // Release the logo from its centered position so it transitions
      // to its final top:1rem spot via the existing .logo CSS transition.
      // Trigger the second-line lag animation at the same moment so
      // "Architects" tucks in behind "The".
      setTimeout(() => {
        l('📍 Move logo to top');
        this.introCentered = false;
        const secondLine = document.querySelector('.logo__line--second');
        if (secondLine) {
          secondLine.classList.add('lag-in');
          setTimeout(() => secondLine.classList.remove('lag-in'), 1000);
        }
      }, 2400);

      // Reveal the slide content and fade out the grid pattern.
      setTimeout(() => {
        l('✨ Show content');
        this.contentVisible = true;
      }, 4000);

      setTimeout(() => {
        l('🛑 Stop grid animation');
        this.fadeOutGrid();
      }, 4000);

      // Mark intro as played for this session
      markIntroPlayed();
    },

    // Reset state (for navigation back to home)
    resetState() {
      l('🔄 Resetting state');
      this.introCentered = true;
      this.contentVisible = false;
      this.navVisible = false;
      this.gridOpacity = 1;
      this.gridPointerEvents = 'auto';
      this.currentWidth = window.innerWidth;
      this.currentHeight = window.innerHeight;
      this.targetWidth = window.innerWidth;
      this.targetHeight = window.innerHeight;
      this.currentStep = 0;

      // Clear any existing timeouts/intervals
      this.cleanupAnimations();
    },

    // Cleanup animations
    cleanupAnimations() {
      if (gridAnimationFrame) {
        cancelAnimationFrame(gridAnimationFrame);
        gridAnimationFrame = null;
      }
      if (gridStepTimeout) {
        clearTimeout(gridStepTimeout);
        gridStepTimeout = null;
      }
      if (fadeInterval) {
        clearInterval(fadeInterval);
        fadeInterval = null;
      }
    },

    // Stop the animation
    stopAnimation() {
      this.animationRunning = false;
      this.cleanupAnimations();
    },

    // Easing function
    easeInOutCubic(t) {
      return t < 0.5
        ? 4 * t * t * t
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    },

    // Animate grid scaling
    animateGrid(grid) {
      if (!this.animationRunning) return;

      const easing = this.easeInOutCubic(0.18);
      const easing2 = this.easeInOutCubic(0.2);

      this.currentWidth += (this.targetWidth - this.currentWidth) * easing;
      this.currentHeight += (this.targetHeight - this.currentHeight) * easing2;

      grid.style.width = `${this.currentWidth}px`;
      grid.style.height = `${this.currentHeight}px`;

      gridAnimationFrame = requestAnimationFrame(() => this.animateGrid(grid));
    },

    // Set grid scale
    setGridScale(x, y) {
      this.targetWidth = window.innerWidth * x;
      this.targetHeight = window.innerHeight * y;
    },

    // Next animation step
    nextStep() {
      if (!this.animationRunning) return;

      const { x, y } = this.sequence[this.currentStep];
      this.setGridScale(x, y);

      this.currentStep = (this.currentStep + 1) % this.sequence.length;
      gridStepTimeout = setTimeout(() => this.nextStep(), 1600);
    },

    // Fade out grid
    fadeOutGrid() {
      let opacity = 1;
      fadeInterval = setInterval(() => {
        opacity -= 0.02;
        this.gridOpacity = opacity;

        if (opacity <= 0) {
          clearInterval(fadeInterval);
          fadeInterval = null;
          this.gridPointerEvents = 'none';
        }
      }, 20);
    },

    // Computed style for grid
    get gridStyle() {
      return `opacity: ${this.gridOpacity}; pointer-events: ${this.gridPointerEvents};`;
    }
  };
}

// Register as Alpine data
Alpine.data('homeIntro', homeIntro);

// Create Alpine store for global access
document.addEventListener('alpine:init', () => {
  const isHome = document.body.classList.contains('home');
  const playLogoCentered = isHome && shouldPlayIntro();

  Alpine.store('homeIntro', {
    // Logo/nav hidden only when playing the full intro.
    navVisible: !playLogoCentered,
    // Only center the logo if playing the full intro.
    introCentered: playLogoCentered,
    // Content hidden only when the full intro plays.
    contentVisible: !playLogoCentered,

    setNavVisible(visible) {
      this.navVisible = visible;
    },
    setIntroCentered(centered) {
      this.introCentered = centered;
    },
    setContentVisible(visible) {
      this.contentVisible = visible;
    }
  });
});
